import { formatOverweightStep, hasBookingPolicies } from "@/lib/ratePolicies";
import { formatDatePlain } from "@/lib/weekDates";
import type { BookingPolicies, OverweightPolicy } from "@/types/api";
import type {
  CardPolicySnapshot,
  RateCarrierSummary,
  RateIngestCarrierPayload,
  RateIngestPayload,
  RateValidityRange,
} from "@/types/feed";

/** Whether a payload is a `rate_cards_ingested` group root. */
export function isRateIngestPayload(
  payload: unknown,
): payload is RateIngestPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "rate_ingest" && Array.isArray(p.carriers);
}

/** Whether a payload is a per-carrier `rate_ingest_carrier` child. */
export function isRateIngestCarrierPayload(
  payload: unknown,
): payload is RateIngestCarrierPayload {
  if (!payload || typeof payload !== "object") return false;
  return (payload as Record<string, unknown>).kind === "rate_ingest_carrier";
}

export interface RateIngestDisplayMeta {
  validityRanges: RateValidityRange[];
  bookingSummary: string | null;
  overweightSummaries: string[];
}

function uniqueStrings(values: Iterable<string>): string[] {
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  }
  return out;
}

function carriersFromPayload(
  payload: RateIngestPayload | RateIngestCarrierPayload,
): RateCarrierSummary[] {
  if (isRateIngestPayload(payload)) return payload.carriers;
  return [
    {
      carrier: payload.carrier,
      rate_card_ids: payload.rate_card_ids,
      contract_refs: payload.contract_refs,
      validity_ranges: payload.validity_ranges,
      policies: payload.policies,
      lines: payload.lines,
      new_lanes: payload.new_lanes,
      updated_lanes: payload.updated_lanes,
      new_validity_buckets: payload.new_validity_buckets,
      median_frt_delta_usd: payload.median_frt_delta_usd,
      top_moves: payload.top_moves,
    },
  ];
}

export function rateIngestContractRefs(
  payload: RateIngestPayload | RateIngestCarrierPayload,
): string[] {
  return uniqueStrings(
    carriersFromPayload(payload).flatMap((carrier) => carrier.contract_refs ?? []),
  );
}

export function payloadHasRateIngestMeta(
  payload: RateIngestPayload | RateIngestCarrierPayload,
): boolean {
  return carriersFromPayload(payload).some(
    (carrier) =>
      carrier.validity_ranges !== undefined || carrier.policies !== undefined,
  );
}

function asBookingPolicies(value: unknown): BookingPolicies | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as BookingPolicies;
  if (!hasBookingPolicies(raw)) return null;
  return {
    max_containers: raw.max_containers ?? null,
    min_containers: raw.min_containers ?? null,
    min_teu: raw.min_teu ?? null,
  };
}

function asOverweightPolicies(value: unknown): OverweightPolicy[] {
  return Array.isArray(value) ? (value as OverweightPolicy[]) : [];
}

export function formatBookingPolicySummary(
  policies: BookingPolicies | null | undefined,
): string | null {
  if (!hasBookingPolicies(policies)) return null;
  const parts: string[] = [];
  if (policies?.max_containers != null) {
    parts.push(
      policies.max_containers === 1
        ? "max 1 ctr"
        : `max ${policies.max_containers} ctr`,
    );
  }
  if (policies?.min_containers != null) {
    parts.push(`min ${policies.min_containers} ctr`);
  }
  if (policies?.min_teu != null) {
    parts.push(`min ${policies.min_teu} TEU`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function formatOverweightPolicySummary(
  policy: OverweightPolicy,
): string | null {
  const origin = policy.origin_label?.trim();
  const dest = policy.destination_label?.trim();
  const route =
    origin && dest
      ? `${origin} → ${dest}`
      : origin
        ? origin
        : dest
          ? `→ ${dest}`
          : policy.origin_country_codes?.length
            ? `ex ${policy.origin_country_codes.join("/")}`
            : "";
  const equipment = [
    (policy.equipment_sizes ?? []).join("/"),
    (policy.equipment_types ?? []).join("/"),
  ]
    .filter(Boolean)
    .join(" ");
  const steps = (policy.steps ?? [])
    .slice(0, 4)
    .map((step) => formatOverweightStep(step, step.currency || policy.currency))
    .join(" / ");
  const summary = [route, equipment, steps].filter(Boolean).join(" · ");
  return summary || null;
}

export function formatValidityRange(
  range: RateValidityRange,
  formatDate: (value: string | null | undefined) => string = formatDatePlain,
): string | null {
  const from = range.valid_from ? formatDate(range.valid_from) : null;
  const to = range.valid_to ? formatDate(range.valid_to) : null;
  const blank = (value: string | null) => !value || value === "—";
  if (!blank(from) && !blank(to)) return `Valid ${from} – ${to}`;
  if (!blank(from)) return `Valid from ${from}`;
  if (!blank(to)) return `Valid through ${to}`;
  return null;
}

export function formatValidityRanges(
  ranges: RateValidityRange[],
  formatDate: (value: string | null | undefined) => string = formatDatePlain,
): string | null {
  const formatted = ranges
    .map((range) => formatValidityRange(range, formatDate))
    .filter((value): value is string => !!value);
  if (formatted.length === 0) return null;
  if (formatted.length === 1) return formatted[0];
  if (formatted.length <= 3) return formatted.join("; ");
  return `${formatted.slice(0, 2).join("; ")}; +${formatted.length - 2} more`;
}

export interface OverweightTierDisplay {
  range: string;
  amount: string;
}

export interface OverweightPolicyDisplay {
  route: string;
  equipment: string | null;
  steps: OverweightTierDisplay[];
}

const STEP_RANGE = /^(\d+(?:\.\d+)?)\s*[–-]\s*(\d+(?:\.\d+)?)[tT]$/i;
const STEP_PLUS = /^(\d+(?:\.\d+)?)[tT]\+$/i;
const AMOUNT = /^(?:USD|EUR|GBP|\$)?\s*[\d,]+(?:\.\d+)?$/i;

function normalizeRange(value: string): string {
  return value.replace(/\s+/g, "").replace(/T/g, "t").replace(/-/g, "–");
}

function normalizeAmount(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/^(?:USD|EUR|GBP|\$)?\s*([\d,]+(?:\.\d+)?)$/i);
  if (!match) return trimmed;
  return `$${match[1]}`;
}

function isStepRange(value: string): boolean {
  const token = value.trim();
  return STEP_RANGE.test(token) || STEP_PLUS.test(token);
}

function parseStepPair(text: string): OverweightTierDisplay | null {
  const parts = text
    .split(/\s+·\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2 && isStepRange(parts[0])) {
    return {
      range: normalizeRange(parts[0]),
      amount: normalizeAmount(parts.slice(1).join(" ")),
    };
  }
  const compact = text.trim().match(
    /^(\d+(?:\.\d+)?\s*[–-]\s*\d+(?:\.\d+)?[tT]|\d+(?:\.\d+)?[tT]\+)\s+(.+)$/i,
  );
  if (compact) {
    return {
      range: normalizeRange(compact[1]),
      amount: normalizeAmount(compact[2]),
    };
  }
  return null;
}

/** Split a stored overweight one-liner into route, equipment, and price tiers. */
export function parseOverweightSummary(line: string): OverweightPolicyDisplay {
  const slashParts = line.split(/\s+\/\s+/).map((part) => part.trim()).filter(Boolean);
  const head = slashParts[0] ?? line;
  const tokens = head
    .split(/\s+·\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const steps: OverweightTierDisplay[] = [];
  const prefix: string[] = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const next = tokens[i + 1];
    if (isStepRange(token) && next && AMOUNT.test(next)) {
      steps.push({ range: normalizeRange(token), amount: normalizeAmount(next) });
      i += 1;
      continue;
    }
    const compact = parseStepPair(token);
    if (compact) {
      steps.push(compact);
      continue;
    }
    prefix.push(token);
  }

  for (const extra of slashParts.slice(1)) {
    const step = parseStepPair(extra);
    if (step) steps.push(step);
  }

  const route =
    prefix.find((part) => part.includes("→") || part.includes("->")) ??
    prefix[0] ??
    line;
  const equipment = prefix
    .filter((part) => part !== route)
    .join(" · ")
    .trim();
  return {
    route,
    equipment: equipment || null,
    steps,
  };
}

export function overweightDisplaysFromMeta(
  meta: RateIngestDisplayMeta,
): OverweightPolicyDisplay[] {
  return meta.overweightSummaries
    .map(parseOverweightSummary)
    .filter((item) => item.route.length > 0);
}

export function bookingPolicyChips(summary: string | null | undefined): string[] {
  if (!summary?.trim()) return [];
  return summary
    .split(/\s+·\s+/)
    .map((part) => {
      const maxCtr = part.match(/^max (\d+) ctr$/i);
      if (maxCtr) {
        return maxCtr[1] === "1"
          ? "Max 1 container"
          : `Max ${maxCtr[1]} containers`;
      }
      const minCtr = part.match(/^min (\d+) ctr$/i);
      if (minCtr) {
        return minCtr[1] === "1"
          ? "Min 1 container"
          : `Min ${minCtr[1]} containers`;
      }
      const minTeu = part.match(/^min ([\d.]+) TEU$/i);
      if (minTeu) return `Min ${minTeu[1]} TEU`;
      return part;
    })
    .filter(Boolean);
}

/** Short collapsed-card teaser — route only, no tier dump. */
export function formatPolicySummaryLines(meta: RateIngestDisplayMeta): string | null {
  const parts: string[] = [];
  const booking = bookingPolicyChips(meta.bookingSummary);
  if (booking.length > 0) parts.push(booking.join(" · "));
  const overweight = overweightDisplaysFromMeta(meta);
  if (overweight.length > 0) {
    const extra =
      overweight.length > 1 ? ` +${overweight.length - 1} more` : "";
    parts.push(`Overweight on ${overweight[0].route}${extra}`);
  }
  return parts.length > 0 ? parts.join(". ") : null;
}

export function metaFromRateIngestPayload(
  payload: RateIngestPayload | RateIngestCarrierPayload,
): RateIngestDisplayMeta {
  const carriers = carriersFromPayload(payload);
  const validityRanges: RateValidityRange[] = [];
  let bookingSummary: string | null = null;
  const overweightSummaries: string[] = [];
  for (const carrier of carriers) {
    for (const range of carrier.validity_ranges ?? []) {
      if (
        (range.valid_from || range.valid_to) &&
        !validityRanges.some(
          (existing) =>
            existing.valid_from === range.valid_from &&
            existing.valid_to === range.valid_to,
        )
      ) {
        validityRanges.push(range);
      }
    }
    const policies = carrier.policies;
    if (!bookingSummary && policies?.booking) bookingSummary = policies.booking;
    for (const line of policies?.overweight ?? []) {
      if (line.trim() && !overweightSummaries.includes(line)) {
        overweightSummaries.push(line);
      }
    }
  }
  return { validityRanges, bookingSummary, overweightSummaries };
}

export function metaFromPolicySnapshots(
  snapshots: CardPolicySnapshot[],
): RateIngestDisplayMeta {
  const validityRanges: RateValidityRange[] = [];
  let bookingSummary: string | null = null;
  const overweightSummaries: string[] = [];
  for (const snapshot of snapshots) {
    if (snapshot.valid_from || snapshot.valid_to) {
      const range = {
        valid_from: snapshot.valid_from,
        valid_to: snapshot.valid_to,
      };
      if (
        !validityRanges.some(
          (existing) =>
            existing.valid_from === range.valid_from &&
            existing.valid_to === range.valid_to,
        )
      ) {
        validityRanges.push(range);
      }
    }
    if (!bookingSummary) {
      bookingSummary = formatBookingPolicySummary(
        asBookingPolicies(snapshot.booking_policies),
      );
    }
    for (const policy of asOverweightPolicies(snapshot.overweight_policies)) {
      const summary = formatOverweightPolicySummary(policy);
      if (summary && !overweightSummaries.includes(summary)) {
        overweightSummaries.push(summary);
      }
    }
  }
  return { validityRanges, bookingSummary, overweightSummaries };
}

function contractSuffix(refs: string[]): string {
  if (refs.length === 0) return "";
  if (refs.length === 1) return ` ${refs[0]}`;
  return ` ${refs[0]} + ${refs.length - 1} more`;
}

export function rateIngestDisplayTitle(
  event: { title: string; payload?: unknown },
): string | null {
  const payload = event.payload;
  if (isRateIngestPayload(payload)) {
    const refs = rateIngestContractRefs(payload);
    if (payload.group.kind === "agent") {
      const named = payload.carriers.map((carrier) => {
        const contract = carrier.contract_refs.find((ref) => ref.trim());
        return contract ? `${carrier.carrier} ${contract}` : carrier.carrier;
      });
      if (named.length === 0) return event.title;
      if (named.length === 1) {
        return `${payload.group.key} sent ${named[0]} rate update`;
      }
      if (named.length <= 3) {
        return `${payload.group.key} sent ${named.join(", ")}`;
      }
      return `${payload.group.key} sent ${named.length} carrier rate updates`;
    }
    if (refs.length === 0) return event.title;
    return `${payload.group.key}${contractSuffix(refs)} rate card ingested`;
  }
  if (isRateIngestCarrierPayload(payload)) {
    const refs = rateIngestContractRefs(payload);
    if (refs.length === 0) return event.title;
    return `${payload.carrier}${contractSuffix(refs)}: ${payload.lines} line${
      payload.lines === 1 ? "" : "s"
    }`;
  }
  return null;
}

export function rateIngestDisplaySummary(
  event: { summary?: string | null; payload?: unknown },
  meta?: RateIngestDisplayMeta | null,
  formatDate: (value: string | null | undefined) => string = formatDatePlain,
): string | null {
  const payload = event.payload;
  if (!isRateIngestPayload(payload) && !isRateIngestCarrierPayload(payload)) {
    return event.summary ?? null;
  }
  const resolved = meta ?? metaFromRateIngestPayload(payload);
  const parts: string[] = [];
  const validity = formatValidityRanges(resolved.validityRanges, formatDate);
  if (validity) parts.push(`${validity}.`);

  if (isRateIngestPayload(payload)) {
    parts.push(
      `${payload.totals.lines} rate line${payload.totals.lines === 1 ? "" : "s"}${
        payload.source_name ? ` via ${payload.source_name}` : ""
      }.`,
    );
  } else if (event.summary) {
    parts.push(event.summary.endsWith(".") ? event.summary : `${event.summary}.`);
  }

  const policies = formatPolicySummaryLines(resolved);
  if (policies) {
    parts.push(policies.endsWith(".") ? policies : `${policies}.`);
  }

  if (parts.length === 0) return event.summary ?? null;
  return parts.join(" ");
}

export function rateIngestCardIds(
  payload: RateIngestPayload | RateIngestCarrierPayload,
): string[] {
  return uniqueStrings(
    carriersFromPayload(payload).flatMap((carrier) => carrier.rate_card_ids ?? []),
  );
}
